import React, { useEffect, useState } from "react";
import { Client } from "../../interfaces/client";
import {
  checkForAvailablePages,
  checkForms,
  getClientByEmail,
} from "../../services/clientService";
import { useAuth } from "../../auth";
import "./Profile.css";
import {
  deleteAdditionalForm,
  downloadBlankUserAgreementForm,
  getAdditionalForm,
  uploadCreditReportForm,
  uploadPageDecoration,
  uploadSignature,
  uploadSSNTraceForm,
  uploadUserAgreement,
} from "../../services/additionalFormService";
import { Service } from "../../interfaces/service";
import {
  getPositionCodes,
  getServiceIDsForPositionCode,
} from "../../services/positionCodeService";
import { getAllServices } from "../../services/serviceService";
import { HttpService } from "../../services/httpService";
import NotificationsPopup from "../../components/client-components/NotificationsPopup";
import { ClientNotification } from "../../interfaces/clientNotification";
import { getNotifications } from "../../services/notificationsService";
import ConfirmPopup from "../../components/client-components/ConfirmPopup";
import { notifyAdminOfClientUpdate } from "../../services/emailService";

const RequiredForms = () => {
  const [client, setClient] = useState<Client | null>(null);
  const { decodeToken } = useAuth();
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [showSignatureUpload, setShowSignatureUpload] = useState(false);
  const [showSSNUpload, setShowSSNUpload] = useState(false);
  const [showCreditUpload, setShowCreditUpload] = useState(false);
  const [loading, setLoading] = useState(true);
  const [signatureUrl, setSignatureUrl] = useState<string | null>(null);
  const [ssnTraceUrl, setSSNTraceUrl] = useState<string | null>(null);
  const [creditReportUrl, setCreditReportUrl] = useState<string | null>(null);
  const [userAgreementUrl, setUserAgreementUrl] = useState<string | null>(null);
  const [pageDecorationUrl, setPageDecorationUrl] = useState<string | null>(
    null,
  );
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const [showAbbreviation, setShowAbbreviation] = useState(false);
  const [missingForms, setMissingForms] = useState("");
  const [notifications, setNotifications] = useState<ClientNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showPageDecoration, setShowPageDecoration] = useState(false);
  const [showConfirmationUpload, setShowConfirmationUpload] = useState(false);
  const [confirmHandlers, setConfirmHandlers] = useState<{
    onConfirm: () => void;
    onCancel: () => void;
  } | null>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const loadData = async () => {
      try {
        const email = decodeToken(localStorage.getItem("token") || "");
        const clientIDString = localStorage.getItem("clientID");
        const clientID = clientIDString ? parseInt(clientIDString, 10) : 0;
        const client = await getClientByEmail(email, clientID);
        setClient(client);

        const { isReferenceSelected } = await checkForAvailablePages(
          client.clientID,
        );
        setShowPageDecoration(isReferenceSelected);

        // Fetch forms
        const signatureForm = await getAdditionalForm(client.clientID, 1);
        if (signatureForm) {
          const signatureUrl = await HttpService.getFile(
            "signatures",
            client.clientID.toString(),
            signatureForm.completedFormUrl,
          );
          setSignatureUrl(signatureUrl);
        }

        const SSNTraceForm = await getAdditionalForm(client.clientID, 3);
        if (SSNTraceForm) {
          const ssnTraceUrl = await HttpService.getFile(
            "ssn-trace-forms",
            client.clientID.toString(),
            SSNTraceForm.completedFormUrl,
          );
          setSSNTraceUrl(ssnTraceUrl);
        }

        const creditReportForm = await getAdditionalForm(client.clientID, 2);
        if (creditReportForm) {
          const creditReportUrl = await HttpService.getFile(
            "credit-report-forms",
            client.clientID.toString(),
            creditReportForm.completedFormUrl,
          );
          setCreditReportUrl(creditReportUrl);
        }

        const userAgreementForm = await getAdditionalForm(client.clientID, 4);
        if (userAgreementForm) {
          const userAgreementUrl = await HttpService.getFile(
            "user-agreements",
            client.clientID.toString(),
            userAgreementForm.completedFormUrl,
          );
          setUserAgreementUrl(userAgreementUrl);
        }

        const pageDecorationForm = await getAdditionalForm(client.clientID, 5);
        if (pageDecorationForm) {
          const pageDecorationUrl = await HttpService.getFile(
            "page-decorations",
            client.clientID.toString(),
            pageDecorationForm.completedFormUrl,
          );
          setPageDecorationUrl(pageDecorationUrl);
        }

        // Fetch position codes and services
        const positionCodes = await getPositionCodes(client.clientID);
        const allServices = await getAllServices();

        const serviceMap = new Map();
        for (const positionCode of positionCodes) {
          const serviceIDs = await getServiceIDsForPositionCode(
            positionCode.positionCodeID,
          );
          serviceMap.set(positionCode.positionCodeID, new Set(serviceIDs));
        }

        // Check which form uploads should be shown
        checkForSpecialServices(serviceMap, allServices);

        const [showAbbreviationResponse, missingForms] = await checkForms(
          client.clientID,
          "client",
        );
        setMissingForms(missingForms);
        setShowAbbreviation(showAbbreviationResponse);

        const notificationsResponse = await getNotifications(client?.clientID);
        notificationsResponse.sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
        setNotifications(notificationsResponse);

        const unreadCount = notificationsResponse.length;
        setUnreadCount(unreadCount);
      } catch (error) {
        console.error("Error fetching client or position codes:", error);
        setErrorMessage("Failed to load data.");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [decodeToken]);

  const checkForSpecialServices = (
    selectedServices: Map<number, Set<number>>,
    allServices: Service[],
  ) => {
    const ssnTraceID = allServices.find(
      (service) => service.serviceID === 17,
    )?.serviceID;
    const creditReportID = allServices.find(
      (service) => service.serviceID === 16,
    )?.serviceID;
    const referenceServiceIDs = allServices
      .filter((service) => service.referenceYears !== null)
      .map((service) => service.serviceID);

    if (ssnTraceID) {
      const isSSNTraceSelected = Array.from(selectedServices.values()).some(
        (serviceSet) => serviceSet.has(ssnTraceID),
      );
      setShowSSNUpload(isSSNTraceSelected);
    }

    if (creditReportID) {
      const isCreditReportSelected = Array.from(selectedServices.values()).some(
        (serviceSet) => serviceSet.has(creditReportID),
      );
      setShowCreditUpload(isCreditReportSelected);
    }

    if (referenceServiceIDs.length > 0) {
      const isReferenceSelected = Array.from(selectedServices.values()).some(
        (serviceSet) =>
          referenceServiceIDs.some((referenceID) =>
            serviceSet.has(referenceID),
          ),
      );
      setShowSignatureUpload(isReferenceSelected);
    }
  };

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleFormUpload = (type: number) => {
    const clientID = client?.clientID;
    if (!clientID) {
      console.error("Client ID is missing");
      return;
    }

    const hasExistingFile =
      (type === 2 && creditReportUrl !== "") ||
      (type === 3 && ssnTraceUrl !== "") ||
      (type === 1 && signatureUrl !== "") ||
      (type === 4 && userAgreementUrl !== "") ||
      (type === 5 && pageDecorationUrl !== "");

    if (hasExistingFile) {
      setConfirmHandlers({
        onConfirm: () => {
          setShowConfirmationUpload(false);
          void startUpload(type);
        },
        onCancel: () => {
          setShowConfirmationUpload(false);
        },
      });
      setShowConfirmationUpload(true);
      return;
    }

    void startUpload(type);
  };

  const startUpload = async (type: number) => {
    const clientID = client?.clientID;
    if (!clientID) {
      console.error("Client ID is missing");
      return;
    }

    try {
      setLoadingSpinner(true);
      const fileInput = document.createElement("input");
      fileInput.type = "file";
      if (type === 1 || type === 5) {
        fileInput.accept = ".png, .jpg, .jpeg";
      } else {
        fileInput.accept = "image/*,.pdf";
      }
      fileInput.onchange = async (event: any) => {
        const file = event.target.files[0];
        if (!file) return;

        if (type === 2) {
          try {
            const response = await uploadCreditReportForm(file, clientID);
            if (response.success) {
              //send email to admin
              await notifyAdminOfClientUpdate(
                client.clientID,
                "Credit Report Form",
              );
              setSuccessMessage("Credit Report form uploaded successfully");
              const updatedCreditReportForm = await getAdditionalForm(
                clientID,
                2,
              );
              if (updatedCreditReportForm) {
                window.location.reload();
              }
            }
          } catch {
            setErrorMessage("Failed to upload Credit Report form");
          }
        } else if (type === 3) {
          try {
            const response = await uploadSSNTraceForm(file, clientID);
            if (response.success) {
              //send email to admin
              await notifyAdminOfClientUpdate(client.clientID, "SSN Form");
              setSuccessMessage("SSN Trace form uploaded successfully");
              const updatedSSNTraceForm = await getAdditionalForm(clientID, 3);
              if (updatedSSNTraceForm) {
                window.location.reload();
              }
            }
          } catch {
            setErrorMessage("Failed to upload SSN Trace form");
          }
        } else if (type === 1) {
          try {
            const response = await uploadSignature(file, clientID);
            if (response.success) {
              //send email to admin
              await notifyAdminOfClientUpdate(
                client.clientID,
                "Signature Form",
              );
              setSuccessMessage("Signature uploaded successfully");
              const updatedSignatureForm = await getAdditionalForm(clientID, 3);
              if (updatedSignatureForm) {
                window.location.reload();
              }
            }
          } catch {
            setErrorMessage("Failed to upload Signature Trace form");
          }
        } else if (type === 4) {
          try {
            const response = await uploadUserAgreement(clientID, file);
            if (response.success) {
              //send email to admin
              await notifyAdminOfClientUpdate(
                client.clientID,
                "User Agreement Form",
              );
              setSuccessMessage("User Agreement uploaded successfully");
              const updatedUserAgreementForm = await getAdditionalForm(
                clientID,
                4,
              );
              if (updatedUserAgreementForm) {
                window.location.reload();
              }
            }
          } catch {
            setErrorMessage("Failed to upload User Agreement form");
          }
        } else if (type === 5) {
          try {
            const response = await uploadPageDecoration(file, clientID);
            if (response.success) {
              //send email to admin
              await notifyAdminOfClientUpdate(client.clientID, "Logo");
              setSuccessMessage("Logo uploaded successfully");
              const updatedPageDecorationForm = await getAdditionalForm(
                clientID,
                5,
              );
              if (updatedPageDecorationForm) {
                window.location.reload();
              }
            }
          } catch {
            setErrorMessage("Failed to upload logo form");
          }
        }
      };
      fileInput.click();
    } catch (error) {
      console.error("Error handling file upload:", error);
    } finally {
      setLoadingSpinner(false);
    }
  };

  const handleDeleteForm = async (formTypeID: number) => {
    try {
      setLoadingSpinner(true);
      if (!client) return;
      const success = await deleteAdditionalForm(client.clientID, formTypeID);

      if (success) {
        setSuccessMessage("Form deleted successfully.");
        window.location.reload();
      }
    } catch (error) {
      console.error("Error deleting form:", error);
      setErrorMessage("An error occurred while deleting the form.");
    } finally {
      setLoadingSpinner(false);
    }
  };

  const handleDownloadBlankUserAgreement = async () => {
    try {
      setLoadingSpinner(true);
      const response = await downloadBlankUserAgreementForm();
      if (response) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        window.open(url, "_blank");
      }
    } catch {
      console.warn("error downloading black user agreement.");
    } finally {
      setLoadingSpinner(false);
    }
  };

  return (
    <div className="content-container">
      <div className="header-container">
        <h1>Required Forms</h1>
        <div className="page-items-container">
          <div className="field-long">
            <label className="field-label">Client Name:</label>
            <div>
              <input
                className="form-control"
                type="text"
                value={client?.clientName || ""}
                disabled
                readOnly
              />
            </div>
          </div>
          <div className="field-long" style={{ flex: "0 0 40%" }}>
            <label className="field-label">Abbreviation/Client Number:</label>
            <div>
              <input
                className="form-control"
                type="text"
                value={
                  showAbbreviation
                    ? client?.abbreviation || ""
                    : "not available."
                }
                disabled
                readOnly
              />
            </div>
          </div>
          <button
            className="notification-button"
            onClick={() => setShowNotifications(true)}
          >
            <i className="bi bi-bell-fill"></i>
            {unreadCount > 0 && (
              <span className="notification-dot">{unreadCount}</span>
            )}
          </button>
        </div>
        {showNotifications && client?.clientID && (
          <NotificationsPopup
            notificationsInput={notifications}
            clientID={client?.clientID}
            onClose={(
              newUnreadCount = unreadCount,
              newNotifications = notifications,
            ) => {
              setNotifications(newNotifications);
              setShowNotifications(false);
              setUnreadCount(newUnreadCount);
            }}
          />
        )}
      </div>

      {loading ? (
        <div className="loading-divider">
          <div className="loading-bar"></div>
        </div>
      ) : (
        <>
          <div className="divider"></div>
          {!showAbbreviation && (
            <div className="text-container error">
              Please complete necessary forms in order to start ordering
              background screening and referencing. Then your
              abbreviation/client number will become visible.{" "}
            </div>
          )}
          {showSignatureUpload && (
            <div
              className={`data-container ${
                missingForms.includes("1") ? "error-border" : ""
              }`}
            >
              <div className="text-header">Reference Form Signature</div>
              <div className="text-body">
                <b>
                  Since you’ve selected a Reference service, a signature is
                  required to complete the reference forms.
                </b>{" "}
                You can upload an existing electronic signature image file, or
                have the primary contact sign their name on a blank white sheet
                of paper and upload it as an image type file (.jpeg, .jpg,
                .heic, .png).
              </div>
              <div className="action-buttons">
                <button
                  className="oxbtn btn-grey"
                  onClick={() => handleFormUpload(1)}
                >
                  Upload Signature
                </button>
                {signatureUrl && (
                  <a
                    href={signatureUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="link"
                  >
                    View Uploaded Signature
                    <i className="btn-icon bi bi-box-arrow-up-right"></i>
                  </a>
                )}
              </div>
            </div>
          )}
          {showSSNUpload && (
            <div
              className={`data-container ${
                missingForms.includes("3") ? "error-border" : ""
              }`}
            >
              <div className="text-header" style={{ flex: "0 0 100%" }}>
                SSN Trace Form
              </div>

              {missingForms.includes("3") ? (
                <div className="text-body">
                  Since you have selected an SSN Trace service, you are required
                  to complete this form. Please complete and upload this form in
                  order to start candidate orders.
                </div>
              ) : (
                <div className="text-body">
                  SSN form not required if credit form was submitted.
                </div>
              )}

              <div className="action-buttons">
                <button
                  className="oxbtn btn-light"
                  onClick={() =>
                    window.open(
                      "https://oxfordstoreacctdev.blob.core.windows.net/documents/SSN Required Forms.pdf",
                      "_blank",
                    )
                  }
                >
                  Print/View Form
                </button>
                <button
                  className="oxbtn btn-grey"
                  onClick={() => handleFormUpload(3)}
                >
                  Upload SSN Trace Form
                </button>
                {ssnTraceUrl && (
                  <a
                    href={ssnTraceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="link"
                  >
                    View Completed Form
                    <i className="btn-icon bi bi-box-arrow-up-right"></i>
                  </a>
                )}
              </div>
            </div>
          )}
          {showCreditUpload && (
            <div
              className={`data-container ${
                missingForms.includes("2") ? "error-border" : ""
              }`}
            >
              <div className="text-header" style={{ flex: "0 0 100%" }}>
                Credit Report Form
              </div>
              <div className="text-body">
                Since you have selected a Credit Report service, you are
                required to complete this form. Please complete and upload this
                form in order to start candidate orders.
              </div>
              <div className="action-buttons">
                <button
                  className="oxbtn btn-light"
                  onClick={() =>
                    window.open(
                      "https://oxfordstoreacctdev.blob.core.windows.net/documents/Credit Required Forms.pdf",
                      "_blank",
                    )
                  }
                >
                  Print/View Form
                </button>
                <button
                  className="oxbtn btn-grey"
                  onClick={() => handleFormUpload(2)}
                >
                  Upload Credit Report Form
                </button>
                {creditReportUrl && (
                  <a
                    href={creditReportUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="link"
                  >
                    View Completed Form
                    <i className="btn-icon bi bi-box-arrow-up-right"></i>
                  </a>
                )}
              </div>
            </div>
          )}
          {/* User Agreement */}
          <div
            className={`data-container ${
              missingForms.includes("4") ? "error-border" : ""
            }`}
          >
            <div className="text-header" style={{ flex: "0 0 100%" }}>
              User Agreement
            </div>

            <div className="text-body">
              User agreement for Background Investigative Reports. A signed and
              dated version is required to be on file for background
              investigations to begin.
            </div>
            <div className="action-buttons">
              <button
                className="oxbtn btn-light"
                onClick={() => handleDownloadBlankUserAgreement()}
              >
                Print/View Form
              </button>
              <button
                className="oxbtn btn-grey"
                onClick={() => handleFormUpload(4)}
              >
                Upload User Agreement Form
              </button>
              {userAgreementUrl && (
                <a
                  href={userAgreementUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link"
                >
                  View Completed Form
                  <i className="btn-icon bi bi-box-arrow-up-right"></i>
                </a>
              )}
            </div>
          </div>
          {/* Logo */}
          {showPageDecoration && (
            <div className="data-container">
              <div className="text-header">Questionnaire Logo</div>
              <div className="text-body">
                Upload a small image that will appear on your candidate and
                reference questionnaires. It will appear in the upper right hand
                corner of the document. You can see a preview of it in
                questionnaires tab. Upload it as an image type file (.jpeg,
                .jpg, .heic, .png).
              </div>
              <div className="action-buttons">
                {pageDecorationUrl && (
                  <button
                    className="oxbtn btn-red"
                    onClick={() => handleDeleteForm(5)}
                  >
                    Remove Logo
                  </button>
                )}

                <button
                  className="oxbtn btn-grey"
                  onClick={() => handleFormUpload(5)}
                >
                  Upload Logo
                </button>
                {pageDecorationUrl && (
                  <a
                    href={pageDecorationUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="link"
                  >
                    View Uploaded Logo
                    <i className="btn-icon bi bi-box-arrow-up-right"></i>
                  </a>
                )}
              </div>
            </div>
          )}
          {successMessage && (
            <div className="alert-message alert alert-success alert-dismissible fade show">
              {successMessage}
              <button
                type="button"
                className="btn-close"
                onClick={() => setSuccessMessage("")}
                aria-label="Close"
              ></button>
            </div>
          )}
          {errorMessage && (
            <div
              className="alert alert-danger alert-dismissible fade show"
              role="alert"
            >
              {errorMessage}
              <button
                type="button"
                className="btn-close"
                onClick={() => setErrorMessage("")}
                aria-label="Close"
              ></button>
            </div>
          )}
        </>
      )}
      {showConfirmationUpload && confirmHandlers && (
        <ConfirmPopup
          onConfirm={confirmHandlers.onConfirm}
          onCancel={confirmHandlers.onCancel}
          message="There is already a form uploaded. Are you sure you would like to replace it?"
        />
      )}
      {loadingSpinner && (
        <div className="loading-mask">
          <div className="loading-spinner"></div>
        </div>
      )}
    </div>
  );
};

export default RequiredForms;
